<template>
  <ValidationProvider :vid="vid" :name="$attrs.label" :rules="rules" v-slot="{ errors }">
    <v-autocomplete
      v-bind="$attrs"
      :items="cleanItems"  
      v-on="$listeners"
      dense
      color="primary"
      :error-messages="errors"
      multiple
      :search-input.sync="search"
      clearable
      :menu-props="{ maxHeight: 400, closeOnContentClick: false }"
      :filter="customFilter"
      :hide-selected="false" 
    >
      <template #item="{ item, attrs }">
        <v-list-item v-bind="attrs" dense @click.stop="toggleItem(item)">
          <v-list-item-action>
            <v-checkbox
              :input-value="isSelected(item)"
              @click.stop="toggleItem(item)"  
              @change="onCheckboxChange($event, item)" 
              :ripple="false"
            />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>
              {{ item[itemTextKey] || item.name || item.text }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.description">
              {{ item.description }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template #selection="{ item }">
        <v-chip small close @click:close="removeItem(item)" class="ma-1" color="success" variant="tonal">
          {{ displayText(item) }}
        </v-chip>
      </template>
    </v-autocomplete>
  </ValidationProvider>
</template>

<script>
export default {
  name: 'MultipleSelectComplete',
  props: {
    rules: String,
    vid: String,
    selectAllLabel: {
      type: String,
      default: 'Select All'
    }
  },
  data: () => ({
    search: ''
  }),
  computed: {
    cleanItems() {
      const items = this.$attrs.items || []
      const byKey = new Map()
      for (const it of items) {
        const key = String(it.name || it.display_name || it.id).toLowerCase()
        const prev = byKey.get(key)
        if (!prev) {
          byKey.set(key, it)
        } else {
          // fica com o mais recente por created_at
          const a = new Date(prev.created_at || 0).getTime()
          const b = new Date(it.created_at || 0).getTime()
          if (b > a) byKey.set(key, it)
        }
      }
      return Array.from(byKey.values())
    },
    itemTextKey () {
      return this.$attrs['item-text'] || 'text';
    },
    itemValueKey () {
      return this.$attrs['item-value'] || 'value';
    },
    allSelected () {
      const items = this.$attrs.items || [];
      const value = this.$attrs.value || [];
      return items.length > 0 && value.length === items.length;
    },
    someSelected () {
      const value = this.$attrs.value || [];
      return value.length > 0 && !this.allSelected;
    },
    icon () {
      if (this.allSelected) return 'mdi-close-box';
      if (this.someSelected) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
    selectedCount () {
      return (this.$attrs.value || []).length;
    }
  },
  methods: {
    clearSelection () {
      this.$emit('input', []);
    },
    onCheckboxChange(checked, item) {
      const selected = this.isSelected(item)
      if (checked && !selected) this.toggleItem(item)
      if (!checked && selected) this.toggleItem(item)
    },
    displayText(item) {
      // respeita item-text, name ou text
      return item?.[this.itemTextKey] ?? item?.name ?? item?.text ?? String(item)
    },
    removeItem(item) {
      const key = this.itemValueKey
      const returnObject = !!this.$attrs['return-object']
      const current = Array.isArray(this.$attrs.value) ? [...this.$attrs.value] : []

      if (returnObject) {
        const idx = current.findIndex(v => (v?.[key]) === item?.[key])
        if (idx >= 0) current.splice(idx, 1)
      } else {
        const val = item?.[key]
        const idx = current.indexOf(val)
        if (idx >= 0) current.splice(idx, 1)
      }

      this.$emit('input', current)
    },
    // Está selecionado?
    isSelected (item) {
      const value = this.$attrs.value || []
      const key = this.itemValueKey
      const returnObject = !!this.$attrs['return-object']

      if (returnObject) {
        return value.some(v => (v?.[key]) === item?.[key])
      }
      return value.includes(item?.[key])
    },

    // Alternar seleção do item
    toggleItem (item) {
      const key = this.itemValueKey
      const returnObject = !!this.$attrs['return-object']
      const current = Array.isArray(this.$attrs.value) ? [...this.$attrs.value] : []

      if (returnObject) {
        const idx = current.findIndex(v => (v?.[key]) === item?.[key])
        if (idx >= 0) current.splice(idx, 1)
        else current.push(item)
      } else {
        const val = item?.[key]
        const idx = current.indexOf(val)
        if (idx >= 0) current.splice(idx, 1)
        else current.push(val)
      }

      this.$emit('input', current)
    },
    toggle () {
      this.$nextTick(() => {
        const items = (this.$attrs.items || []);
        const returnObject = !!this.$attrs['return-object'];

        if (this.allSelected) {
          this.$emit('input', []);
          return;
        }

        const selectedValues = returnObject
          ? items.slice()
          : items.map(it => it[this.itemValueKey]);

        this.$emit('input', selectedValues);
      });
    },
    // Match por display_name OU name (case-insensitive); se não definires item-text, usa fallback
    customFilter (item, queryText, itemText) {
      if (!queryText) return true;

      const q = String(queryText).toLowerCase();
      const t1 = String(item[this.itemTextKey] || '').toLowerCase();
      const t2 = String(item.name || '').toLowerCase(); // útil para permissões (name técnico)

      // inclui também o texto calculado de Vuetify (itemText)
      const t3 = String(itemText || '').toLowerCase();

      return t1.includes(q) || t2.includes(q) || t3.includes(q);
    }
  }
}
</script>
